import React from "react";
import { AnimatePresence, motion } from "framer-motion";

/**
 * Premium iPhone 15 Pro-style mockup.
 *
 * Two render modes:
 *  1. `image` prop → renders a real screenshot filling the entire screen area.
 *     Image changes crossfade smoothly via stacked motion layers.
 *  2. `children` → renders custom HTML inside an inset screen (with overlay
 *     status bar + Dynamic Island).
 *
 * Props:
 *  - width: px (default 280) — maintains true 19.5:9 aspect ratio
 *  - image: string URL (optional)
 *  - alt: string (for image)
 *  - className / style: passthrough for transforms
 *  - children: ReactNode (alternative to image)
 *  - testId: data-testid
 *  - priority: boolean — if true, image loads eagerly (hero)
 *  - imgKey: string — used as React key for crossfade animation
 */
export default function IPhoneMockup({
  width = 280,
  image,
  alt = "",
  className = "",
  style = {},
  children,
  testId = "iphone-mockup",
  priority = false,
  imgKey,
}) {
  const aspect = 19.5 / 9;
  const height = Math.round(width * aspect);
  const radius = Math.round(width * 0.17);
  const innerRadius = Math.max(radius - 6, 24);

  return (
    <div
      className={`iphone-frame ${className}`}
      data-testid={testId}
      style={{ width, height, borderRadius: radius, ...style }}
    >
      {/* Side buttons */}
      <span className="iphone-btn iphone-btn-left" style={{ top: height * 0.18 }} />
      <span className="iphone-btn iphone-btn-left" style={{ top: height * 0.26, height: height * 0.04 }} />
      <span className="iphone-btn iphone-btn-left" style={{ top: height * 0.32, height: height * 0.04 }} />
      <span className="iphone-btn iphone-btn-right" style={{ top: height * 0.22, height: height * 0.07 }} />

      <div className="iphone-screen" style={{ borderRadius: innerRadius }}>
        {image ? (
          <AnimatePresence mode="sync" initial={false}>
            <motion.img
              key={imgKey || image}
              src={image}
              alt={alt}
              className="iphone-screen-img"
              loading={priority ? "eager" : "lazy"}
              fetchPriority={priority ? "high" : "auto"}
              decoding="async"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.55, ease: [0.22, 1, 0.36, 1] }}
              style={{ position: "absolute", inset: 0 }}
            />
          </AnimatePresence>
        ) : (
          <>
            <div className="iphone-island" aria-hidden="true" />
            <div className="iphone-status">
              <span>9:41</span>
              <span className="flex items-center gap-1.5">
                <span className="status-dot" />
                <span className="status-bars">
                  <span /><span /><span /><span />
                </span>
                <span className="status-batt"><span /></span>
              </span>
            </div>
            <div className="iphone-screen-content">{children}</div>
          </>
        )}
      </div>
    </div>
  );
}
